/**
 * Iter C.2.1.d — Compact summary of a line item's commercial commitments.
 *
 * Per Rahul's 2026-02 stabilization direction, this component is
 * **deliberately quiet**: legacy commercial presentation remains the
 * primary user experience. Commitments exist for governance, cascade,
 * AMC, invoicing, and reporting — but they do NOT force a visual
 * redesign on the proposal/PO detail surfaces.
 *
 * Default rendering: a single small "▸ N commitments" chip that operators
 * can click to expand inline. Collapsed state takes one row of negligible
 * height. The full breakdown is always available in the Edit Line Item
 * dialog via the CommitmentsEditor.
 *
 * Used on:
 *   • ProposalDetail.jsx — under the product name in the line items table
 *   • PurchaseOrderDetail.jsx — same placement on the PO detail page
 *
 * If the line has no commitments, the component renders nothing.
 * Backward-compatible: legacy lines (no commitments[]) are unaffected.
 */
import React, { useState } from "react";
import { CaretRight, CaretDown } from "@phosphor-icons/react";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function CommitmentsBadgeStrip({ commitments }) {
    const [open, setOpen] = useState(false);
    const list = Array.isArray(commitments) ? commitments : [];
    if (list.length === 0) return null;

    return (
        <div className="mt-1" data-testid="commitments-badge-strip">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-800 transition-colors"
                data-testid="commitments-toggle"
                aria-expanded={open}
                title="Internal commercial commitment breakdown (R-B*)"
            >
                {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                <span className="italic">
                    {list.length} internal commitment{list.length === 1 ? "" : "s"}
                </span>
            </button>
            {open && (
                <div className="ml-3 mt-1 space-y-0.5 text-[10px] text-slate-500" data-testid="commitments-expanded">
                    {list.map((c, idx) => (
                        <div key={c.id || `idx-${idx}`} data-testid={`commitment-chip-${idx}`}>
                            <span className="text-slate-700">{c.label || "—"}</span>
                            {c.cadence ? <span className="text-slate-400"> · {c.cadence}</span> : null}
                            <span className="font-mono text-slate-600"> · {fmtINR(c.amount)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CommitmentsBadgeStrip;
